// Independent smoke expectations: do not import the application's access
// predicate here, or the probe could repeat the same authorization bug.
export const REFERRAL_ADMIN_ROLES = [
  'admin',
  'district_admin',
  'school_admin',
  'admin_504',
  'sped_chair',
  'sped_chair_all'
]
export const REFERRAL_TEAM_ROLES = [...REFERRAL_ADMIN_ROLES, 'school_psych']
export const REFERRAL_CM_ROLES = ['case_manager', 'case_manager_full', 'speech_case_manager']
export const REFERRAL_BROAD_STAFF_ROLES = [
  'teacher_full',
  'case_manager_full',
  'staff_view',
  'staff_edit',
  'staff_edit_docs'
]
export const REFERRAL_PROBE_ROLES = [
  ...new Set([
    ...REFERRAL_TEAM_ROLES,
    ...REFERRAL_CM_ROLES,
    ...REFERRAL_BROAD_STAFF_ROLES,
    'teacher',
    'paraeducator',
    'service_provider'
  ])
]
export const REFERRAL_FIXTURES = ['pending', 'owned', 'live504']
export const REFERRAL_STAGES = ['before consent', 'consent saved', 'consent cleared']

export function expectedReferralRead(role, fixture, stage) {
  if (!REFERRAL_PROBE_ROLES.includes(role))
    throw new Error(`Unsupported referral probe role: ${role}`)
  return (
    REFERRAL_TEAM_ROLES.includes(role) ||
    fixture === 'live504' ||
    stage === 'consent saved' ||
    (fixture === 'owned' && REFERRAL_CM_ROLES.includes(role))
  )
}

export function expectedReferralCopyRead(role, fixture, stage, kind, testingAccess = false) {
  const standard = fixture === 'live504' || stage === 'consent saved'
  if (kind === 'extendedAccessStudents') return REFERRAL_ADMIN_ROLES.includes(role) || standard
  if (kind === 'testingAccommodations')
    return (
      REFERRAL_TEAM_ROLES.includes(role) ||
      (standard && (REFERRAL_BROAD_STAFF_ROLES.includes(role) || testingAccess))
    )
  throw new Error(`Unknown referral copy: ${kind}`)
}

// Each descriptor is an independently authorized Firestore query. CMs union
// their normal staff rows, plan-owned rows and pre-consent evaluation owners.
export function studentReadQueryFilters(role, uid) {
  if (REFERRAL_TEAM_ROLES.includes(role)) return [[]]
  if (!REFERRAL_PROBE_ROLES.includes(role)) return []
  const standard = [['app.staffAccess', '==', true]]
  if (!REFERRAL_BROAD_STAFF_ROLES.includes(role))
    standard.push(['app.staffIds', 'array-contains', uid])
  const filters = [standard]
  if (REFERRAL_CM_ROLES.includes(role)) {
    filters.push([['app.studentData.caseManagerId', '==', uid]])
    filters.push([['app.referralOwnerIds', 'array-contains', uid]])
  }
  return filters
}

// Only explicit authorization denial proves a negative test. Expired auth,
// missing files/indexes, network failures and endpoint 404s must fail the run.
export async function assertReferralAccess(allowed, operation) {
  try {
    await operation()
  } catch (error) {
    if (
      !allowed &&
      [
        'permission-denied',
        'storage/unauthorized',
        'functions/permission-denied',
        'http/403'
      ].includes(error?.code)
    )
      return 'denied, as expected'
    throw error
  }
  if (!allowed) throw new Error('SECURITY: restricted referral access unexpectedly succeeded')
  return 'allowed, as expected'
}
